import { useCallback, useEffect, useRef, useState } from 'react';
import { Position, POSITION } from 'models/Position';
import { getScrollParent } from 'utils';
import useMatchMedia from 'hooks/useMatchMedia';

const ARROW_WIDTH = 12;
const MOBILE_HORIZONTAL_OFFSET = 16;
const VIEWPORT_SIDE_OFFSET = 16;
// Keeps the arrow off the banner's rounded corners at either end.
const ARROW_EDGE_OFFSET = 20;

// The arrow is drawn on the banner, so it only lines up with the anchor if it
// is told where the anchor ended up: the banner is nudged and clamped away
// from it on every side.
const ARROW_OFFSET_VARIABLE = '--floating-banner-arrow-offset';

const getClamped = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(max, min));

// What is actually on screen, in the page coordinates the banner is placed in.
// The visual viewport is what the phone keyboard shrinks, so a banner clamped
// against it stays in front of the keyboard rather than behind it.
const getVisibleViewport = () => {
  const { visualViewport } = window;

  return {
    top: visualViewport?.pageTop ?? window.scrollY,
    left: visualViewport?.pageLeft ?? window.scrollX,
    width: visualViewport?.width ?? window.innerWidth,
    height: visualViewport?.height ?? window.innerHeight,
  };
};

const getClampedLeft = (left: number, contentWidth: number) => {
  const { left: viewportLeft, width } = getVisibleViewport();

  return getClamped(
    left,
    viewportLeft + VIEWPORT_SIDE_OFFSET,
    viewportLeft + width - contentWidth - VIEWPORT_SIDE_OFFSET,
  );
};

// Only for a banner beside its anchor: it is centred on the anchor rather
// than butted against it, so sliding it into view keeps it attached — the
// arrow slides along the same edge to follow.
const getClampedTop = (top: number, contentHeight: number) => {
  const { top: viewportTop, height } = getVisibleViewport();

  return getClamped(
    top,
    viewportTop + VIEWPORT_SIDE_OFFSET,
    viewportTop + height - contentHeight - VIEWPORT_SIDE_OFFSET,
  );
};

// What of the anchor's scroll container is actually on screen, in client
// coordinates. A page that scrolls the window has no scrollable ancestor to
// be clipped by — its container is the whole document — so without the screen
// itself nothing tells an anchor apart from one that has been scrolled past.
const getVisibleContainerRect = (scrollContainer: HTMLElement) => {
  const { visualViewport } = window;

  const viewportTop = visualViewport?.offsetTop ?? 0;
  const viewportLeft = visualViewport?.offsetLeft ?? 0;
  const { width, height } = getVisibleViewport();

  const { top, left, right, bottom } = scrollContainer.getBoundingClientRect();

  return {
    top: Math.max(top, viewportTop),
    left: Math.max(left, viewportLeft),
    right: Math.min(right, viewportLeft + width),
    bottom: Math.min(bottom, viewportTop + height),
  };
};

const getArrowOffset = (anchorCenter: number, start: number, size: number) => {
  // Never past the middle: a banner shorter than two corners keeps its arrow
  // centred rather than having it pulled to one end.
  const edgeOffset = Math.min(ARROW_EDGE_OFFSET, size / 2);

  return getClamped(anchorCenter - start, edgeOffset, size - edgeOffset);
};

const getVerticalTop = (
  position: Position,
  anchorRect: DOMRect,
  contentHeight: number,
) =>
  position === POSITION.BOTTOM
    ? anchorRect.bottom + window.scrollY + ARROW_WIDTH
    : anchorRect.top + window.scrollY - contentHeight - ARROW_WIDTH;

const getHorizontalPosition = (
  anchorRect: DOMRect,
  contentWidth: number,
  contentHeight: number,
  hasSpaceOnRight: boolean,
) =>
  ({
    left:
      window.scrollX +
      (hasSpaceOnRight
        ? anchorRect.left + anchorRect.width + ARROW_WIDTH
        : anchorRect.left - contentWidth - ARROW_WIDTH),
    top:
      anchorRect.y + window.scrollY + (anchorRect.height - contentHeight) / 2,
    bannerPosition: hasSpaceOnRight ? POSITION.RIGHT : POSITION.LEFT,
  } as const);

const useFloatingBannerPositioning = ({
  onHover,
  preferredPosition,
  setIsInViewport,
}: {
  onHover?: boolean;
  preferredPosition?: Position;
  setIsInViewport: (value: boolean) => void;
}) => {
  const [position, setPosition] = useState(null);
  const [isPositioned, setIsPositioned] = useState(false);

  // The side the banner ended up on drives the arrow, so it has to survive as
  // state: scrolling can flip the banner from one side of the anchor to the
  // other, and a ref would leave the arrow pointing the way it did on mount.
  const [bannerPosition, setBannerPosition] = useState<Position>(
    POSITION.RIGHT,
  );

  const animationFrameRef = useRef<number>(null);
  const currentPositionRef = useRef(null);

  const anchorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const isPhablet = useMatchMedia('isPhablet');

  const updatePosition = useCallback(
    (left: number, top: number, arrowOffset: number) => {
      const content = contentRef.current;

      // Set on the node rather than through state: the moves that only mutate
      // style never re-render, and the arrow has to follow those too.
      content?.style.setProperty(ARROW_OFFSET_VARIABLE, `${arrowOffset}px`);

      const currentPosition = currentPositionRef.current;

      if (currentPosition?.left === left && currentPosition?.top === top)
        return;

      currentPositionRef.current = { left, top };

      if (content && position) {
        content.style.left = `${left}px`;
        content.style.top = `${top}px`;
        return;
      }

      setPosition({ left, top });
    },
    [position],
  );

  const getPreferredVerticalPosition = useCallback(
    (hasSpaceBelow: boolean, hasSpaceAbove: boolean) =>
      preferredPosition === POSITION.BOTTOM &&
      hasSpaceBelow &&
      (!isPhablet || hasSpaceAbove)
        ? POSITION.BOTTOM
        : preferredPosition === POSITION.TOP && hasSpaceAbove
        ? POSITION.TOP
        : null,
    [isPhablet, preferredPosition],
  );

  const getAutoVerticalPosition = useCallback(
    (
      hasSpaceOnRightBelow: boolean,
      hasSpaceAbove: boolean,
      hasSpaceBelow: boolean,
    ) =>
      isPhablet && !hasSpaceOnRightBelow && hasSpaceAbove
        ? POSITION.TOP
        : hasSpaceBelow
        ? POSITION.BOTTOM
        : POSITION.TOP,
    [isPhablet],
  );

  const getDefaultVerticalPosition = useCallback(
    (
      position: Position,
      anchorRect: DOMRect,
      contentWidth: number,
      centeredLeft: number,
      contentHeight: number,
      hasSpaceOnRightBelow: boolean,
      hasSpaceOnLeftCentered: boolean,
    ) => {
      const shouldBeLeft = isPhablet || !hasSpaceOnLeftCentered;

      const { left, top, bottom, width } = anchorRect || {};

      return {
        left: shouldBeLeft
          ? window.scrollX +
            (hasSpaceOnRightBelow
              ? left - MOBILE_HORIZONTAL_OFFSET
              : left + width - contentWidth + MOBILE_HORIZONTAL_OFFSET)
          : centeredLeft,
        top:
          position === POSITION.BOTTOM
            ? bottom + window.scrollY + ARROW_WIDTH
            : top + window.scrollY - contentHeight - ARROW_WIDTH,
      };
    },
    [isPhablet],
  );

  const calculateAndUpdateBannerPosition = useCallback(
    (anchorRect: DOMRect, contentRect: DOMRect) => {
      const viewportWidth = window.innerWidth;
      const { width: contentWidth, height: contentHeight } = contentRect;

      if (!contentWidth || !contentHeight) return false;

      const spaceOnRight = viewportWidth - (anchorRect.x + anchorRect.width);

      const hasSpaceOnRight = spaceOnRight >= contentWidth + ARROW_WIDTH;
      const hasSpaceOnLeft = anchorRect.x >= contentWidth + ARROW_WIDTH;
      const hasSpaceBelow =
        window.innerHeight -
          (anchorRect.bottom + contentHeight + ARROW_WIDTH) >=
        0;
      const hasSpaceAbove = anchorRect.top - contentHeight - ARROW_WIDTH >= 0;

      const centeredTop =
        anchorRect.top +
        window.scrollY +
        anchorRect.height / 2 -
        contentHeight / 2;

      const hasSpaceAboveCentered = centeredTop >= 0;
      const hasSpaceBelowCentered =
        centeredTop + contentHeight <= window.innerHeight + window.scrollY;

      const centeredLeft =
        anchorRect.left +
        window.scrollX +
        anchorRect.width / 2 -
        contentWidth / 2;

      const hasSpaceOnLeftCentered = centeredLeft >= 0;
      const hasSpaceOnRightBelow =
        spaceOnRight + anchorRect.width >= contentWidth;

      const canUseHorizontalPosition =
        (hasSpaceOnLeft || hasSpaceOnRight) &&
        hasSpaceAboveCentered &&
        hasSpaceBelowCentered;

      const preferredVerticalPosition = getPreferredVerticalPosition(
        hasSpaceBelow,
        hasSpaceAbove,
      );

      // Where the arrow has to end up, in the same document coordinates the
      // banner is placed in.
      const anchorCenterX =
        anchorRect.left + window.scrollX + anchorRect.width / 2;
      const anchorCenterY =
        anchorRect.top + window.scrollY + anchorRect.height / 2;

      if (preferredVerticalPosition || !canUseHorizontalPosition) {
        const autoVerticalPosition = getAutoVerticalPosition(
          hasSpaceOnRightBelow,
          hasSpaceAbove,
          hasSpaceBelow,
        );

        const verticalPosition =
          preferredVerticalPosition || autoVerticalPosition;

        const { left, top } = preferredVerticalPosition
          ? {
              left: centeredLeft,
              top: getVerticalTop(verticalPosition, anchorRect, contentHeight),
            }
          : getDefaultVerticalPosition(
              verticalPosition,
              anchorRect,
              contentWidth,
              centeredLeft,
              contentHeight,
              hasSpaceOnRightBelow,
              hasSpaceOnLeftCentered,
            );

        const clampedLeft = getClampedLeft(left, contentWidth);

        setBannerPosition(verticalPosition);
        updatePosition(
          clampedLeft,
          // Only kept out of the negative space above the document, which
          // nothing could scroll to. A banner sitting above or below its
          // anchor cannot be nudged along that axis at all: the arrow is on
          // the edge that touches the anchor, so any shift tears it off — an
          // anchor scrolled out of view takes its banner with it instead.
          Math.max(top, 0),
          getArrowOffset(anchorCenterX, clampedLeft, contentWidth),
        );
        return true;
      }

      const {
        left,
        top,
        bannerPosition: nextBannerPosition,
      } = getHorizontalPosition(
        anchorRect,
        contentWidth,
        contentHeight,
        hasSpaceOnRight,
      );

      const clampedTop = getClampedTop(top, contentHeight);

      setBannerPosition(nextBannerPosition);
      updatePosition(
        getClampedLeft(left, contentWidth),
        clampedTop,
        getArrowOffset(anchorCenterY, clampedTop, contentHeight),
      );
      return true;
    },
    [
      getAutoVerticalPosition,
      getDefaultVerticalPosition,
      getPreferredVerticalPosition,
      updatePosition,
    ],
  );

  const updateIfAnchorInViewport = useCallback(
    (anchor: HTMLElement) => {
      if (onHover) return;

      const anchorRect = anchor.getBoundingClientRect();
      const containerRect = getVisibleContainerRect(getScrollParent(anchor));

      const inXViewport =
        anchorRect.right > containerRect.left &&
        anchorRect.left < containerRect.right;

      const inYViewport =
        anchorRect.bottom > containerRect.top &&
        anchorRect.top < containerRect.bottom;

      setIsInViewport(inXViewport && inYViewport);
    },
    [onHover, setIsInViewport],
  );

  // only closes the gate — `position` is deliberately left in place so a banner
  // that is fading out does not lose its coordinates and jump mid-fade
  const resetIsPositioned = useCallback(() => setIsPositioned(false), []);

  const resetAndRetry = useCallback((retry: () => void) => {
    currentPositionRef.current = null;
    setPosition(null);
    setIsPositioned(false);
    retry();
  }, []);

  const updateBannerPosition = useCallback(
    (shouldRetry = true) => {
      const scheduleRetry = () => {
        if (!shouldRetry) return;

        if (animationFrameRef.current) {
          window.cancelAnimationFrame(animationFrameRef.current);
        }

        animationFrameRef.current = window.requestAnimationFrame(() => {
          animationFrameRef.current = null;
          updateBannerPosition(false);
        });
      };

      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = window.requestAnimationFrame(() => {
        animationFrameRef.current = null;

        const anchor = anchorRef.current;
        const content = contentRef.current;

        if (!anchor || !content) {
          resetAndRetry(scheduleRetry);
          return;
        }

        updateIfAnchorInViewport(anchor);

        const anchorRect = anchor.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();
        const hasPosition = calculateAndUpdateBannerPosition(
          anchorRect,
          contentRect,
        );

        if (!hasPosition) {
          resetAndRetry(scheduleRetry);
          return;
        }

        setIsPositioned(true);
      });
    },
    [calculateAndUpdateBannerPosition, resetAndRetry, updateIfAnchorInViewport],
  );

  useEffect(
    () => () => {
      if (animationFrameRef.current)
        window.cancelAnimationFrame(animationFrameRef.current);
    },
    [],
  );

  return {
    position,
    isPositioned,
    bannerPosition,
    anchorRef,
    contentRef,
    updateBannerPosition,
    resetIsPositioned,
  };
};

export default useFloatingBannerPositioning;
